if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      selectionState = null;
      navigationPending = false;
      selectionPaymentStates = new Map();

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.onSelectionSubmit = (event) => {
          if (!this.selectionState || !this.selectionForms?.includes(event.target)) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          if (this.selectionState === 'error') this.selectionFeedback?.focus();
        };
        document.addEventListener('submit', this.onSelectionSubmit, true);
        this.onSelectionPageShow = (event) => {
          if (event.persisted && this.navigationPending) window.location.reload();
        };
        window.addEventListener('pageshow', this.onSelectionPageShow);

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.abortController?.abort();
        document.removeEventListener('submit', this.onSelectionSubmit, true);
        window.removeEventListener('pageshow', this.onSelectionPageShow);
        this.selectionPaymentStates.clear();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
          newNode.dispatchEvent(new CustomEvent('starbond:product:rendered', { bubbles: true }));
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target) || this.navigationPending) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        if (shouldFetchFullPage && viewMode === 'main-product') {
          const destination = this.buildProductNavigationUrl(productUrl, selectedOptionValues);
          const controller = this.abortController;
          this.abortController = undefined;
          controller?.abort();
          this.latestSelectionRequest = null;
          this.pendingRequestUrl = null;
          this.navigationPending = true;
          this.lockSelectionPurchase();
          window.location.assign(destination);
          return;
        }

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          allowSingleVariant: shouldSwapProduct,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      buildProductNavigationUrl(productUrl, optionValues) {
        const destination = new URL(productUrl, window.location.href);
        destination.searchParams.delete('section_id');
        destination.searchParams.delete('variant');
        if (optionValues.length) {
          destination.searchParams.set('option_values', optionValues.join(','));
        } else {
          destination.searchParams.delete('option_values');
        }
        const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (previewThemeId && !destination.searchParams.has('preview_theme_id')) {
          destination.searchParams.set('preview_theme_id', previewThemeId);
        }
        return destination.href;
      }

      get productForm() {
        return this.querySelector('form.main-product-form') || this.querySelector('form[is="product-form"]');
      }

      get selectionStickyBar() {
        if (this.dataset.updateUrl !== 'true') return null;
        return document.querySelector(`sticky-atc-bar[data-product-id="${this.productId}"]`);
      }

      lockSelectionPurchase() {
        this.selectionState = 'loading';
        this.selectionForms = [this.productForm, this.selectionStickyBar?.querySelector('form')].filter(Boolean);
        this.selectionForms.forEach((form) => {
          form.setAttribute('aria-busy', 'true');
          form.querySelectorAll('[name="add"], input[type="submit"]').forEach((button) => {
            button.disabled = true;
            button.classList.add('btn--loading');
          });
          form.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout').forEach((payment) => {
            if (!this.selectionPaymentStates.has(payment)) this.selectionPaymentStates.set(payment, payment.inert);
            payment.inert = true;
          });
        });
        this.showSelectionFeedback(false);
      }

      releaseSelectionPurchase(canPurchase, variant) {
        this.selectionState = null;
        this.selectionForms?.forEach((form) => {
          form.removeAttribute('aria-busy');
          form.querySelectorAll('[name="add"], input[type="submit"]').forEach((button) => {
            button.disabled = !canPurchase;
            button.classList.remove('btn--loading');
          });
        });
        this.selectionPaymentStates.forEach((wasInert, payment) => {
          if (payment.isConnected) payment.inert = !canPurchase || wasInert;
          if (canPurchase || !payment.isConnected) this.selectionPaymentStates.delete(payment);
        });
        this.selectionStickyBar?.updateButton(!canPurchase,
          variant ? FoxTheme.variantStrings.soldOut : FoxTheme.variantStrings.unavailable);
        if (this.selectionFeedback) {
          this.selectionFeedback.hidden = true;
          this.selectionFeedback.style.display = 'none';
        }
      }

      showSelectionFeedback(failed) {
        const form = this.productForm;
        if (!form) return;
        if (!this.selectionFeedback?.isConnected) {
          const feedback = document.createElement('div');
          feedback.className = 'product-selection-feedback alert blocks-radius grid gap-2';
          feedback.setAttribute('tabindex', '-1');
          const message = document.createElement('p');
          message.className = 'm-0';
          const retry = document.createElement('button');
          retry.type = 'button';
          retry.className = 'btn btn--secondary';
          retry.textContent = FoxTheme.variantStrings.selectionRetry;
          retry.addEventListener('click', () => {
            if (this.latestSelectionRequest) this.renderProductInfo(this.latestSelectionRequest);
          });
          feedback.append(message, retry);
          form.before(feedback);
          this.selectionFeedback = feedback;
        }
        const feedback = this.selectionFeedback;
        feedback.classList.toggle('alert--error', failed);
        feedback.setAttribute('role', failed ? 'alert' : 'status');
        feedback.querySelector('p').textContent = failed
          ? FoxTheme.variantStrings.selectionError : FoxTheme.variantStrings.selectionLoading;
        feedback.querySelector('button').hidden = !failed;
        feedback.querySelector('button').style.display = failed ? '' : 'none';
        feedback.hidden = false;
        feedback.style.display = '';
        if (failed) feedback.focus();
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);
          this.currentVariant = variant;

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
              data: { sectionId: this.sectionId, html, variant: null },
            });
            return;
          }

          this.updateMedia(variant, html);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut
          );

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            })
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      async renderProductInfo(request) {
        if (this.navigationPending) return;
        const { requestUrl, targetId, callback } = request;
        this.latestSelectionRequest = request;
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;
        this.lockSelectionPurchase();
        const timeout = setTimeout(() => controller.abort(), 15000);

        try {
          const response = await fetch(requestUrl, { signal: controller.signal });
          if (!response.ok) throw new Error('Product selection request failed');
          const responseText = await response.text();
          if (this.abortController !== controller || !this.isConnected) return;
          if (controller.signal.aborted) throw new Error('Product selection request timed out');
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const quickView = this.dataset.viewMode === 'quick-view'
            ? html.querySelector('#MainProduct-quick-view__content')?.content : null;
          const product = (quickView || html).querySelector('product-info');
          if (!product) {
            throw new Error('Product selection response is incomplete');
          }
          const form = product.querySelector('form.main-product-form') || product.querySelector('form[is="product-form"]');
          const addButton = form?.querySelector('[name="add"]');
          const formVariantId = Number(form?.querySelector('[name="id"]')?.value);
          const variantMarker = product.querySelector('variant-selects [data-selected-variant]');
          let variant;
          if (variantMarker) {
            variant = this.getSelectedVariant(product);
          } else if (request.allowSingleVariant && !product.querySelector('variant-selects') &&
              Number.isSafeInteger(formVariantId) && formVariantId > 0 && addButton) {
            variant = { id: formVariantId, available: !addButton.disabled };
          } else {
            throw new Error('Product selection response is incomplete');
          }
          if (variant && (!Number.isSafeInteger(variant.id) || variant.id <= 0 || typeof variant.available !== 'boolean' ||
              String(form?.querySelector('[name="id"]')?.value) !== String(variant.id))) {
            throw new Error('Product selection response does not match its form');
          }
          const canPurchase = variant?.available === true && !!addButton && !addButton.disabled;
          this.pendingRequestUrl = null;
          callback(html);
          if (this.abortController !== controller) return;
          this.releaseSelectionPurchase(canPurchase, variant);
          document.getElementById(targetId)?.focus();
        } catch (error) {
          if (this.abortController !== controller || !this.isConnected) return;
          this.selectionState = 'error';
          this.selectionForms?.forEach((form) => {
            form.removeAttribute('aria-busy');
            form.querySelectorAll('[name="add"], input[type="submit"]').forEach((button) => {
              button.disabled = true;
              button.classList.remove('btn--loading');
            });
          });
          this.showSelectionFeedback(true);
        } finally {
          clearTimeout(timeout);
        }
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant, html) {
        let productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return;
        const nextMedia = html?.querySelector(`[id^="MediaGallery-${this.sectionId}"]`);
        if (nextMedia && productMedia.dataset.mediaKey !== nextMedia.dataset.mediaKey) {
          const replacement = nextMedia.cloneNode(true);
          if (this.sectionId !== this.dataset.section) {
            replacement.innerHTML = replacement.innerHTML.replaceAll(this.sectionId, this.dataset.section);
            replacement.id = productMedia.id;
          }
          productMedia.replaceWith(replacement);
          productMedia = replacement;
        }
        this.mediaUpdateToken = (this.mediaUpdateToken || 0) + 1;
        const token = this.mediaUpdateToken;
        customElements.whenDefined('media-gallery').then(() => {
          if (this.isConnected && productMedia.isConnected && token === this.mediaUpdateToken) {
            productMedia.init();
            productMedia.setActiveMedia(variant);
          }
        });
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}
